import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useWeather } from '../../hooks/useWeather';
import PageHeader from '../../components/PageHeader/PageHeader';

import { ReactComponent as Healthcare1 } from '../../assets/site-assets/work/healthcare-1.svg';
import { ReactComponent as Healthcare2 } from '../../assets/site-assets/work/healthcare-2.svg';
import { ReactComponent as Healthcare3 } from '../../assets/site-assets/work/healthcare-3.svg';
import { ReactComponent as Healthcare4 } from '../../assets/site-assets/work/healthcare-4.svg';
import { ReactComponent as Healthcare5 } from '../../assets/site-assets/work/healthcare-5.svg';
import { ReactComponent as WeatherIcon }  from '../../assets/site-assets/work/weather.svg';
import { ReactComponent as TimeIcon }     from '../../assets/site-assets/work/time.svg';
import { ReactComponent as CurrencyIcon } from '../../assets/site-assets/work/cuurrency.svg';
import { ReactComponent as WeekendIcon }  from '../../assets/site-assets/work/weekend.svg';
import { ReactComponent as StarIcon }     from '../../assets/site-assets/work/star.svg';
import { ReactComponent as Vector9 }      from '../../assets/site-assets/work/Vector-9.svg';
import { ReactComponent as AirportLogo }  from '../../assets/site-assets/work/airport.svg';
import { ReactComponent as AirArabiaLogo} from '../../assets/site-assets/work/air-aribia.svg';

import BgImage   from '../../assets/site-assets/work/bg.webp';
import CityImage from '../../assets/site-assets/work/Image.webp';
import TravelBg  from '../../assets/site-assets/work/travel-to-bg.webp';

import './WorkPage.scss';

const healthcareStatIcons = [Healthcare1, Healthcare2, Healthcare3, Healthcare4, Healthcare5];

const WeatherTab = ({ t }) => {
  const { data, loading } = useWeather();

  if (loading) {
    return (
      <div className="work-tab-content work-tab-content--weather">
        <div className="work-tab-content__weather-skeleton" />
        <div className="work-tab-content__weather-skeleton work-tab-content__weather-skeleton--wide" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="work-tab-content work-tab-content--weather">
        <div className="work-tab-content__temp-block">
          <span className="work-tab-content__period">{t('work.mustKnow.tabContent.weather.period1')}</span>
          <span className="work-tab-content__temp">{t('work.mustKnow.tabContent.weather.temp1')}</span>
          <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.weather.label1')}</span>
        </div>
        <div className="work-tab-content__divider" />
        <div className="work-tab-content__temp-block">
          <span className="work-tab-content__period">{t('work.mustKnow.tabContent.weather.period2')}</span>
          <span className="work-tab-content__temp">{t('work.mustKnow.tabContent.weather.temp2')}</span>
          <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.weather.label2')}</span>
        </div>
        <p className="work-tab-content__note">{t('work.mustKnow.tabContent.weather.note')}</p>
      </div>
    );
  }

  return (
    <div className="work-tab-content work-tab-content--weather">
      <div className="work-tab-content__temp-block">
        <span className="work-tab-content__period">{t('work.mustKnow.tabContent.weather.live.nowLabel')}</span>
        <span className="work-tab-content__temp">{data.tempC}°C</span>
        <span className="work-tab-content__temp-sub">{data.description}</span>
      </div>
      <div className="work-tab-content__divider" />
      <div className="work-tab-content__temp-block">
        <span className="work-tab-content__period">{t('work.mustKnow.tabContent.weather.live.feelsLikeLabel')}</span>
        <span className="work-tab-content__temp">{data.feelsLikeC}°C</span>
        <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.weather.live.humidityLabel')}: {data.humidity}%</span>
      </div>
      <p className="work-tab-content__note">
        {t('work.mustKnow.tabContent.weather.live.todayLabel')}: {data.minC}°C – {data.maxC}°C
        <br />
        {t('work.mustKnow.tabContent.weather.live.windLabel')}: {data.windKmph} km/h
      </p>
    </div>
  );
};

const tabDefs = [
  { id: 'weather',  Icon: WeatherIcon  },
  { id: 'timezone', Icon: TimeIcon     },
  { id: 'currency', Icon: CurrencyIcon },
  { id: 'weekend',  Icon: WeekendIcon  },
];

const WorkPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('weather');

  /* tabContent must be inside the component to access t() */
  const tabContent = {
    weather: <WeatherTab t={t} />,
    timezone: (
      <div className="work-tab-content work-tab-content--dummy">
        <div className="work-tab-content__temp-block">
          <span className="work-tab-content__period">{t('work.mustKnow.tabContent.timezone.period')}</span>
          <span className="work-tab-content__temp">{t('work.mustKnow.tabContent.timezone.value')}</span>
          <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.timezone.sub')}</span>
        </div>
        <p className="work-tab-content__note">{t('work.mustKnow.tabContent.timezone.note')}</p>
      </div>
    ),
    currency: (
      <div className="work-tab-content work-tab-content--dummy">
        <div className="work-tab-content__temp-block">
          <span className="work-tab-content__period">{t('work.mustKnow.tabContent.currency.period')}</span>
          <span className="work-tab-content__temp">{t('work.mustKnow.tabContent.currency.value')}</span>
          <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.currency.sub')}</span>
        </div>
        <p className="work-tab-content__note">{t('work.mustKnow.tabContent.currency.note')}</p>
      </div>
    ),
    weekend: (
      <div className="work-tab-content work-tab-content--dummy">
        <div className="work-tab-content__temp-block">
          <span className="work-tab-content__period">{t('work.mustKnow.tabContent.weekend.period')}</span>
          <span className="work-tab-content__temp">{t('work.mustKnow.tabContent.weekend.value')}</span>
          <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.weekend.sub')}</span>
        </div>
        <p className="work-tab-content__note">{t('work.mustKnow.tabContent.weekend.note')}</p>
      </div>
    ),
    attire: (
      <div className="work-tab-content work-tab-content--dummy">
        <div className="work-tab-content__temp-block">
          <span className="work-tab-content__period">{t('work.mustKnow.tabContent.attire.period')}</span>
          <span className="work-tab-content__temp">{t('work.mustKnow.tabContent.attire.value')}</span>
          <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.attire.sub')}</span>
        </div>
        <p className="work-tab-content__note">{t('work.mustKnow.tabContent.attire.note')}</p>
      </div>
    ),
    safety: (
      <div className="work-tab-content work-tab-content--dummy">
        <div className="work-tab-content__temp-block">
          <span className="work-tab-content__period">{t('work.mustKnow.tabContent.safety.period')}</span>
          <span className="work-tab-content__temp">{t('work.mustKnow.tabContent.safety.value')}</span>
          <span className="work-tab-content__temp-sub">{t('work.mustKnow.tabContent.safety.sub')}</span>
        </div>
        <p className="work-tab-content__note">{t('work.mustKnow.tabContent.safety.note')}</p>
      </div>
    ),
  };

  return (
    <div className="work-page">

      <PageHeader
        bgImage={BgImage}
        title={t('work.pageTitle')}
        subtitle={t('work.subtitle')}
        description={t('work.description')}
      />

      {/* ── Living In Sharjah ── */}
      <section className="work-living">
        <div className="work-container">
          <div className="work-living__grid">

            <div className="work-living__text">
              <span className="work-living__label">{t('work.living.label')}</span>
              <h2 className="work-living__city">{t('work.living.city')}</h2>
              <h3 className="work-living__subtitle">
                {t('work.living.subtitleLine1')}<br />
                {t('work.living.subtitleLine2')}<br />
                {t('work.living.subtitleLine3')}
              </h3>
              <p className="work-living__body">{t('work.living.body1')}</p>
              <p className="work-living__body">{t('work.living.body2')}</p>
              <p className="work-living__body">{t('work.living.body3')}</p>
            </div>

            <div className="work-living__visual">
              <Vector9 className="work-living__house-icon" aria-hidden="true" />
              <img src={CityImage} alt={t('work.living.imgAlt')} className="work-living__img" />
            </div>

          </div>
        </div>

        {/* ── 22% Stat Row ── */}
        <section className="work-stat">
          <div className="work-stat__grid work-container">
            <div className="work-stat__card work-stat__card--orange">
              <p className="work-stat__card-text">{t('work.living.statText')}</p>
              <span className="work-stat__note">{t('work.living.statNote')}</span>
            </div>
            <div className="work-stat__card work-stat__card--white">
              <span className="work-stat__num">{t('work.living.statNum')}<sup>%</sup></span>
            </div>
          </div>
        </section>
      </section>

      {/* ── Healthcare + Icons + Info ── */}
      <section className="work-hc-section">

        <div className="work-container work-hc-section__card-wrap">
          <div className="work-hc-card">
            <div className="work-hc-card__left">
              <h2 className="work-hc-card__title">{t('work.healthcare.title')}</h2>
              <span className="work-hc-card__title-sub">
                <Trans i18nKey="work.healthcare.sub" components={{ span: <span /> }} />
              </span>
              <p className="work-hc-card__tagline">
                {t('work.healthcare.taglineLine1')}<br />{t('work.healthcare.taglineLine2')}
              </p>
            </div>
            <div className="work-hc-card__right">
              <p className="work-hc-card__body">{t('work.healthcare.body1')}</p>
              <p className="work-hc-card__body">{t('work.healthcare.body2')}</p>
            </div>
          </div>
        </div>

        <div className="work-hc-stats">
          <div className="work-container">
            <div className="work-hc-stats__row">
              {healthcareStatIcons.map((Icon, i) => (
                <div key={i} className="work-hc-stats__item">
                  <div className="work-hc-stats__arch">
                    <Icon className="work-hc-stats__icon" />
                    <span className="work-hc-stats__num">{t(`work.healthcare.stats.${i}.num`)}</span>
                    <span className="work-hc-stats__label">{t(`work.healthcare.stats.${i}.label`)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="work-hc-info">
          <div className="work-container work-hc-info__grid">
            <div className="work-hc-info__text">
              <h3 className="work-hc-info__heading">
                <Trans i18nKey="work.healthcare.heading2" components={{ span: <span />, br: <br /> }} />
              </h3>
              <p className="work-hc-info__body">{t('work.healthcare.body3')}</p>
              <p className="work-hc-info__body">{t('work.healthcare.body4')}</p>
            </div>
            <div className="work-hc-info__card">
              <h4 className="work-hc-info__card-title">
                <Trans i18nKey="work.healthcare.cardTitle" components={{ span: <span /> }} />
              </h4>
              <div className="work-hc-info__hospitals">
                <ul className="work-hc-info__list">
                  {Array.from({ length: 9 }, (_, i) => (
                    <li key={i}>{t(`work.healthcare.hospitalsLeft.${i}`)}</li>
                  ))}
                </ul>
                <ul className="work-hc-info__list">
                  {Array.from({ length: 9 }, (_, i) => (
                    <li key={i}>{t(`work.healthcare.hospitalsRight.${i}`)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* ── Must-know Information (tabbed) ── */}
      <section className="work-mustknow">
        <StarIcon className="work-mustknow__star" aria-hidden="true" />

        <div className="work-container work-mustknow__content">
          <h2 className="work-mustknow__title">
            <Trans i18nKey="work.mustKnow.title" components={{ span: <span /> }} />
          </h2>

          <div className="work-mustknow__card">
            <div className="work-mustknow__tabs">
              {tabDefs.map(({ id, Icon }) => (
                <button
                  key={id}
                  className={`work-mustknow__tab${activeTab === id ? ' work-mustknow__tab--active' : ''}`}
                  onClick={() => setActiveTab(id)}
                >
                  <Icon className="work-mustknow__tab-icon" />
                  <span>{t(`work.mustKnow.tabs.${id}`)}</span>
                </button>
              ))}
            </div>
            <div className="work-mustknow__panel">
              {tabContent[activeTab]}
            </div>
          </div>
        </div>
      </section>

      {/* ── Travel To Sharjah ── */}
      <section className="work-travel-section" style={{ backgroundImage: `url(${TravelBg})` }}>
        <div className="work-container work-travel-section__content">
          <div className="work-travel-section__text">
            <span className="work-travel-section__label">{t('work.travel.label')}</span>
            <h2 className="work-travel-section__city">{t('work.travel.city')}</h2>
            <p className="work-travel-section__subtitle">
              <Trans i18nKey="work.travel.subtitle" components={{ strong: <strong />, br: <br /> }} />
            </p>
            <p className="work-travel-section__body">{t('work.travel.body1')}</p>
            <p className="work-travel-section__body">{t('work.travel.body2')}</p>
          </div>
        </div>
      </section>

      {/* ── Transport: Airport + Air Arabia ── */}
      <section className="work-transport">
        <div className="work-container">

          <div className="row align-items-center work-transport__row mb-4 mb-lg-5">
            <div className="col-12 col-lg-6 order-2 order-lg-1">
              <div className="work-transport__card">
                <h3 className="work-transport__card-title">
                  <Trans i18nKey="work.transport.airport.title" components={{ span: <span /> }} />
                </h3>
                <p className="work-transport__card-body">{t('work.transport.airport.body1')}</p>
                <p className="work-transport__card-body">{t('work.transport.airport.body2')}</p>
                <a href={t('work.transport.airport.url')} className="work-transport__card-btn" target="_blank" rel="noopener noreferrer">{t('work.transport.airport.link')}</a>
              </div>
            </div>
            <div className="col-12 col-lg-6 d-flex justify-content-center mt-4 mt-lg-0 mb-4 mb-lg-0 order-1 order-lg-2">
              <AirportLogo className="work-transport__logo" />
            </div>
          </div>

          <div className="row align-items-center work-transport__row justify-content-end">
            <div className="col-12 col-lg-6 d-flex justify-content-center mb-4 mb-lg-0 order-1 order-lg-1">
              <AirArabiaLogo className="work-transport__logo work-transport__logo--arabia" />
            </div>
            <div className="col-12 col-lg-6 order-2 order-lg-2">
              <div className="work-transport__card">
                <h3 className="work-transport__card-title">
                  <Trans i18nKey="work.transport.airArabia.title" components={{ span: <span /> }} />
                </h3>
                <p className="work-transport__card-body">{t('work.transport.airArabia.body1')}</p>
                <p className="work-transport__card-body">{t('work.transport.airArabia.body2')}</p>
                <p className="work-transport__card-body">{t('work.transport.airArabia.body3')}</p>
                <a href={t('work.transport.airArabia.url')} className="work-transport__card-btn" target="_blank" rel="noopener noreferrer">{t('work.transport.airArabia.link')}</a>
              </div>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

export default WorkPage;
